import { apiFetch } from "@/lib/api-client";
import type { SparklinePoint } from "@/components/ui/sparkline";

/**
 * Träger-portal API-typen — server-side schema-mirror.
 * Quelle: api/internal/http/handler/traeger.go (Phase 15.C + 16.D).
 */

export type ComplianceStatus = "green" | "yellow" | "red";

/**
 * Lizenz-status PRO child-org. ORTHOGONAL zu compliance_status (B11):
 * compliance_status ist faktisch ein DOKU-status, dieses feld ist der
 * LIZENZ-status. Eine lizenz-gesperrte Einrichtung ist kein doku-problem.
 *
 * ⚠️ Backend liefert dieses feld HEUTE NOCH NICHT (siehe BE-followup):
 * traegerChildHeatSummary in api/internal/http/handler/traeger.go hat
 * kein license-feld. Bis dahin ist es zur laufzeit `undefined` → das UI
 * behandelt das als "unknown" und täuscht KEIN "aktiv/grün" vor.
 */
export type ChildLicenseStatus = "active" | "expired";

export interface TraegerChildSummary {
  org_id: string;
  org_name: string;
  tier: string;
  max_indoor_temp_c: number;
  max_outdoor_temp_c: number | null; // nullable: kein outdoor-wert → null (backend FIX 5)
  critical_count: number;
  muss_count: number;
  soll_count: number;
  total_entries: number;
  distinct_days: number;
  last_entry_at?: string;
  compliance_status: ComplianceStatus;
  /**
   * B11: per-child lizenz-status, orthogonal zu compliance_status.
   * Optional, weil das backend es noch nicht liefert (BE-followup) — bis
   * dahin runtime-`undefined`, vom UI als "unknown" gerendert (kein fake-grün).
   */
  license_status?: ChildLicenseStatus;
  /**
   * Phase 16.D: SparklinePoint[] per day (max 30 days window) für
   * /traeger/vergleich-sparklines. Optional — empty wenn keine entries
   * im period.
   */
  daily_trend?: SparklinePoint[];
}

export interface TraegerDashboardResponse {
  traeger_name: string;
  traeger_id: string;
  child_count: number;
  max_children: number;
  min_children: number;
  generated_at: string;
  period_label: string;
  child_summary: TraegerChildSummary[];
}

export interface TraegerChildListItem {
  id: string;
  name: string;
  tier: "S" | "M" | "L";
  is_active: boolean;
  plz?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  created_at: string;
  user_count: number;
  room_count: number;
  entries_30d: number;
}

export interface TraegerOrgsListResponse {
  items: TraegerChildListItem[];
  max_orgs: number;
  min_orgs: number;
  current_count: number;
}

export interface TraegerCompareResponse {
  period: string;
  since: string;
  until: string;
  children: TraegerChildSummary[];
}

export interface CreateChildOrgInput {
  name: string;
  tier: "S" | "M" | "L";
  plz: string;
  city: string;
  latitude?: number;
  longitude?: number;
  heat_officer_email?: string;
}

export async function fetchTraegerDashboard(): Promise<TraegerDashboardResponse> {
  return apiFetch<TraegerDashboardResponse>("/v1/traeger/dashboard");
}

export async function fetchTraegerOrgs(): Promise<TraegerOrgsListResponse> {
  return apiFetch<TraegerOrgsListResponse>("/v1/traeger/orgs");
}

export async function createChildOrg(
  input: CreateChildOrgInput,
): Promise<{ id: string; name: string; tier: string; created_at: string }> {
  return apiFetch("/v1/traeger/orgs", {
    method: "POST",
    body: input,
  });
}

export async function deactivateChildOrg(id: string): Promise<void> {
  await apiFetch(`/v1/traeger/orgs/${id}`, { method: "DELETE" });
}

export async function fetchTraegerCompare(
  period: "7d" | "30d" | "quarter" | "year" | "season",
): Promise<TraegerCompareResponse> {
  return apiFetch<TraegerCompareResponse>(
    `/v1/traeger/compare?period=${encodeURIComponent(period)}`,
  );
}
